import { Icon } from '@aqarmap/aqarmap-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '~/components/Tooltip';
import { ValuationItemType } from '~/types/valuationTypes';

const ReturnOnInvestment = ({
  valuationData,
}: {
  valuationData: ValuationItemType;
}) => {
  const { t } = useTranslation('app');
  const valuationPrice = valuationData.currentValuation.valuationPrice;
  const purchasePrice = valuationData.purchasePrice;
  const confidenceRate = Number(
    valuationData.currentValuation.confidenceRate * 100,
  ).toFixed(0);
  const returnValue = valuationPrice - purchasePrice;
  const returnRate = Number(
    purchasePrice > 0 ? (returnValue / purchasePrice) * 100 : 0,
  ).toFixed(2);

  const data = React.useMemo(
    () => [
      {
        key: 'purchasePrice',
        name: t('purchasePriceText'),
        infoName: t('purchasePriceInfoNameText'),
        bgColor: 'bg-gray_light_1',
        value: (
          <p className="text-title_4 flex gap-1">
            <span>{purchasePrice.toLocaleString()}</span>
            <span>{t('pound')}</span>
          </p>
        ),
      },
      {
        key: 'valuationPrice',
        name: t('valuationPriceText'),
        infoName: t('valuationPriceInfoNameText', {
          confidenceRate,
        }),
        bgColor: 'bg-gray_light_1',
        value: (
          <p className="text-title_4 flex gap-1 ">
            <span>{valuationPrice.toLocaleString()}</span>
            <span>{t('pound')}</span>
          </p>
        ),
      },
      {
        key: 'returnRate',
        name: t('returnOnInvestment'),
        infoName: t('returnOnInvestmentInfo'),
        bgColor: 'bg-[#1B5E201F]',
        value: (
          <div
            className={`w-full flex items-center gap-1 ${
              returnValue > 0 ? 'text-green_dark_1' : 'text-red_dark'
            }`}
          >
            <Icon name={returnValue > 0 ? 'up-trend' : 'down-trend'} />
            <p
              className={`w-max text-title_4 flex gap-1 ${
                returnValue > 0 ? 'text-green_dark_1' : 'text-red_dark'
              }`}
              dir={returnValue >= 0 ? 'rtl' : 'ltr'}
            >
              <span
                dir={returnValue >= 0 ? 'rtl' : 'ltr'}
              >{`${Number(returnRate).toLocaleString()}${returnValue > 0 ? '%' : ''}${returnValue > 0 ? '+' : '%'}`}</span>
            </p>
          </div>
        ),
      },
    ],
    [t, purchasePrice, valuationPrice, confidenceRate, returnValue, returnRate],
  );

  return (
    <div className="flex flex-col gap-3x">
      <p className="text-title_4 text-gray__dark_2">
        {t('returnOnInvestmentTitle')}
      </p>
      <div className="flex flex-col md:flex-row gap-2x md:gap-3x">
        {data.map((item) => (
          <div
            key={item.key}
            className={`flex-1 flex flex-col gap-x px-3x py-2x md:py-3x rounded-[8px] ${item.bgColor}`}
          >
            <div className="flex gap-x items-center">
              <p className="text-body_2 text-gray__dark_2">{item.name}</p>
              <Tooltip
                className="max-w-[272px]"
                id={item.key}
                text={item.infoName ?? ''}
              >
                <Icon name="info" size="16px" className="text-blue_dark_1" />
              </Tooltip>
            </div>
            {item.value}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReturnOnInvestment;
